import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import api, {
  formatApiError,
  getCurrentUserProfile,
  getWorkflows,
  createWorkflow,
  updateWorkflow,
  deleteWorkflow,
  getTenantSubdomain,
  getOperationDeleteInfo,
  deleteOperation,
} from '../services/api';
import { decimalToHHMM, hhmmToDecimal, validateHHMM } from '../utils/timeFormat';

/** Quick presets for Standard Time (HH:MM) to improve UX */
const STANDARD_TIME_PRESETS = [
  { labelKey: 'operations.preset30Min', value: '00:30' },
  { labelKey: 'operations.preset1H', value: '01:00' },
  { labelKey: 'operations.preset2H', value: '02:00' },
  { labelKey: 'operations.preset4H', value: '04:00' },
  { labelKey: 'operations.preset8H', value: '08:00' },
];

/** Parse HH:MM string to { hours, minutes }. Handles empty or partial input. */
function parseHHMMToParts(hhmm) {
  if (!hhmm || typeof hhmm !== 'string') return { hours: 0, minutes: 0 };
  const trimmed = hhmm.trim();
  if (!trimmed) return { hours: 0, minutes: 0 };
  const parts = trimmed.split(':');
  const hours = Math.max(0, parseInt(parts[0], 10) || 0);
  const minutes = parts.length > 1 ? Math.min(59, Math.max(0, parseInt(parts[1], 10) || 0)) : 0;
  return { hours, minutes };
}

/** Build HH:MM string from hours and minutes. */
function partsToHHMM(hours, minutes) {
  const h = Math.max(0, Math.floor(hours));
  const m = Math.min(59, Math.max(0, Math.floor(minutes)));
  return `${h}:${String(m).padStart(2, '0')}`;
}

/**
 * TimePicker-inspired Standard Time spinner: two fields (Hours, Minutes) with vertical +/- buttons.
 * value/onChange are HH:MM string; hasError and errorId optional for a11y.
 */
function StandardTimeSpinner({ value, onChange, hasError, ariaErrorId, ariaHintId }) {
  const { t } = useTranslation(['admin', 'common']);
  const { hours, minutes } = parseHHMMToParts(value);
  const update = (newHours, newMinutes) => {
    onChange(partsToHHMM(newHours, newMinutes));
  };
  const incHours = () => update(hours + 1, minutes);
  const decHours = () => update(Math.max(0, hours - 1), minutes);
  const incMinutes = () => update(hours, minutes >= 59 ? 0 : minutes + 1);
  const decMinutes = () => update(hours, minutes <= 0 ? 59 : minutes - 1);
  const handleHoursInput = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') { update(0, minutes); return; }
    update(Math.max(0, parseInt(raw, 10) || 0), minutes);
  };
  const handleMinutesInput = (e) => {
    const raw = e.target.value.replace(/\D/g, '');
    if (raw === '') { update(hours, 0); return; }
    update(hours, Math.min(59, parseInt(raw, 10) || 0));
  };
  const inputClass = `w-11 h-full text-center text-sm font-mono font-semibold bg-transparent text-slate-900 outline-none selection:bg-primary/20 border-0 rounded-none ${
    hasError ? 'text-red-700' : ''
  }`;
  const wrapperClass = `flex items-stretch h-12 rounded-lg border overflow-hidden shadow-sm transition-all focus-within:ring-2 focus-within:ring-primary/30 focus-within:border-primary ${
    hasError ? 'border-red-300 bg-red-50/50' : 'border-slate-200 bg-slate-50'
  }`;
  const btnClass = 'flex items-center justify-center w-8 shrink-0 text-slate-500 hover:text-slate-800 hover:bg-slate-200/80 transition-colors border-slate-200';
  return (
    <div className="inline-flex items-center gap-1.5">
      <div className={wrapperClass}>
        <input
          type="text"
          inputMode="numeric"
          value={String(hours).padStart(2, '0')}
          onChange={handleHoursInput}
          onBlur={() => update(hours, minutes)}
          maxLength={4}
          aria-label={t('common:hours')}
          className={inputClass}
          aria-describedby={[ariaHintId, ariaErrorId].filter(Boolean).join(' ') || undefined}
        />
        <div className="flex flex-col border-l border-slate-200">
          <button type="button" onClick={incHours} aria-label={t('operations.increaseHours')} className={`${btnClass} border-b border-slate-200`}>
            <span className="material-symbols-outlined text-[18px]">expand_less</span>
          </button>
          <button type="button" onClick={decHours} aria-label={t('operations.decreaseHours')} className={btnClass}>
            <span className="material-symbols-outlined text-[18px]">expand_more</span>
          </button>
        </div>
      </div>
      <span className="text-lg font-mono font-bold text-slate-400 select-none">:</span>
      <div className={wrapperClass}>
        <input
          type="text"
          inputMode="numeric"
          value={String(minutes).padStart(2, '0')}
          onChange={handleMinutesInput}
          onBlur={() => update(hours, minutes)}
          maxLength={2}
          aria-label={t('operations.minutes')}
          className={inputClass}
          aria-describedby={[ariaHintId, ariaErrorId].filter(Boolean).join(' ') || undefined}
        />
        <div className="flex flex-col border-l border-slate-200">
          <button type="button" onClick={incMinutes} aria-label={t('operations.increaseMinutes')} className={`${btnClass} border-b border-slate-200`}>
            <span className="material-symbols-outlined text-[18px]">expand_less</span>
          </button>
          <button type="button" onClick={decMinutes} aria-label={t('operations.decreaseMinutes')} className={btnClass}>
            <span className="material-symbols-outlined text-[18px]">expand_more</span>
          </button>
        </div>
      </div>
    </div>
  );
}

const OperationsPage = ({ onNavigate }) => {
  const { t } = useTranslation(['admin', 'common']);
  const isRsTenant = (getTenantSubdomain() || '').toLowerCase() === 'rs';
  const [selectedOperation, setSelectedOperation] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState('');
  const [productionLineFilter, setProductionLineFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const [operations, setOperations] = useState([]);
  const [operationsCount, setOperationsCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showComments, setShowComments] = useState(true);
  const [pageSize, setPageSize] = useState(20);
  const [loadingOperations, setLoadingOperations] = useState(false);
  const [operationsError, setOperationsError] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [availableWorkflows, setAvailableWorkflows] = useState([]);
  const [workflowsList, setWorkflowsList] = useState([]);

  // Workflow codes for display in selector (operations with no workflows stay empty)
  const availableWorkflowCodes = availableWorkflows.length > 0 ? availableWorkflows.map(w => w.code) : [];
  const workflowCodesForDisplay = availableWorkflowCodes.length > 0 ? availableWorkflowCodes : ['GCR', 'GIR', 'PROMO', 'BYNOW'];

  // Form state for editing operation
  const [editFormData, setEditFormData] = useState({
    production_line_lp: '',
    activity: '',
    domain: '',
    program: '',
    production_line_lp: '',
    production_type: '',
    description: '',
    temps_standard: '',
    allows_multiple_insertion: true,
    max_duplicates: 5,
    is_reference_enabled: true,
    workflows: [],
    comment: '',
  });

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // State for add operation modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [showManageWorkflowsModal, setShowManageWorkflowsModal] = useState(false);
  const [newWorkflowForm, setNewWorkflowForm] = useState({
    code: '',
    project_code: '',
    digits_in_project_code: 7,
    allow_alphabetic_chars: true,
  });
  const [addingWorkflow, setAddingWorkflow] = useState(false);
  const [workflowAddError, setWorkflowAddError] = useState(null);
  const [editingWorkflowId, setEditingWorkflowId] = useState(null);
  const [editWorkflowForm, setEditWorkflowForm] = useState({ code: '', label: '', project_code: '', digits_in_project_code: 7, allow_alphabetic_chars: true });
  const [workflowToDelete, setWorkflowToDelete] = useState(null);
  const [deletingWorkflow, setDeletingWorkflow] = useState(false);
  const [showDeleteOperationModal, setShowDeleteOperationModal] = useState(false);
  const [operationDeleteInfo, setOperationDeleteInfo] = useState(null);
  const [deleteRelatedMode, setDeleteRelatedMode] = useState('reassign');
  const [deleteTargetOperationId, setDeleteTargetOperationId] = useState('');
  const [deleteInfoLoading, setDeleteInfoLoading] = useState(false);
  const [deleteSubmitLoading, setDeleteSubmitLoading] = useState(false);
  const [deleteModalError, setDeleteModalError] = useState(null);
  const [addFormData, setAddFormData] = useState({
    production_line_lp: '',
    activity: '',
    domain: '',
    program: '',
    production_line_lp: '',
    production_type: '',
    description: '',
    temps_standard: '',
    is_active: true,
    allows_multiple_insertion: true,
    max_duplicates: 5,
    is_reference_enabled: true,
    workflows: [],
    comment: '',
  });
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);
  
  // Domain combobox state for add modal
  const [addDomainDropdownOpen, setAddDomainDropdownOpen] = useState(false);
  const [addDomainSearchQuery, setAddDomainSearchQuery] = useState('');

  // Get unique domains, production lines, and production types for filters/dropdowns
  const [uniqueDomains, setUniqueDomains] = useState([]);
  const [uniqueProductionLines, setUniqueProductionLines] = useState([]);
  const [uniqueProductionTypes, setUniqueProductionTypes] = useState([]);
  
  // Domain combobox state
  const [domainDropdownOpen, setDomainDropdownOpen] = useState(false);
  const [domainSearchQuery, setDomainSearchQuery] = useState('');

  // Production type dropdown state (edit and add)
  const [productionTypeDropdownOpen, setProductionTypeDropdownOpen] = useState(false);
  const [addProductionTypeDropdownOpen, setAddProductionTypeDropdownOpen] = useState(false);

  const selectedOperationData = operations.find(op => op.id === selectedOperation);
  
  // Filter domains for add modal
  const filteredAddDomains = uniqueDomains.filter(domain =>
    domain.toLowerCase().includes(addDomainSearchQuery.toLowerCase())
  );
  
  // Check if current input is a new domain for add modal
  const isNewAddDomain = addDomainSearchQuery && 
    !uniqueDomains.some(d => d.toLowerCase() === addDomainSearchQuery.toLowerCase());

  // Filter production type options for edit and add forms (dynamic from existing operations)
  const filteredProductionTypes = uniqueProductionTypes.filter(opt =>
    opt.toLowerCase().includes((editFormData.production_type || '').toLowerCase())
  );
  const filteredAddProductionTypes = uniqueProductionTypes.filter(opt =>
    opt.toLowerCase().includes((addFormData.production_type || '').toLowerCase())
  );

  // New production type (not in existing operations list) for edit and add
  const isNewProductionType = (editFormData.production_type || '').trim() &&
    !uniqueProductionTypes.some(o => o.toLowerCase() === (editFormData.production_type || '').trim().toLowerCase());
  const isNewAddProductionType = (addFormData.production_type || '').trim() &&
    !uniqueProductionTypes.some(o => o.toLowerCase() === (addFormData.production_type || '').trim().toLowerCase());

  // Initialize form data when operation is selected
  useEffect(() => {
    if (selectedOperationData) {
      const domainValue = selectedOperationData.domain || '';
      // Handle temps_standard: backend returns HH:MM string, but check if it's a number for backward compatibility
      let tempsStandardValue = '00:00';
      if (selectedOperationData.temps_standard) {
        if (typeof selectedOperationData.temps_standard === 'string') {
          // Already in HH:MM format from backend
          tempsStandardValue = selectedOperationData.temps_standard;
        } else if (typeof selectedOperationData.temps_standard === 'number') {
          // Convert decimal to HH:MM (backward compatibility)
          tempsStandardValue = decimalToHHMM(selectedOperationData.temps_standard);
        }
      }
      setEditFormData({
        production_line_lp: selectedOperationData.production_line_lp || '',
        activity: selectedOperationData.activity || '',
        domain: domainValue,
        program: selectedOperationData.program || '',
        production_line_lp: selectedOperationData.production_line_lp || '',
        production_type: selectedOperationData.production_type || '',
        description: selectedOperationData.description || '',
        temps_standard: tempsStandardValue,
        allows_multiple_insertion: selectedOperationData.allows_multiple_insertion !== undefined ? selectedOperationData.allows_multiple_insertion : true,
        max_duplicates: typeof selectedOperationData.max_duplicates === 'number' ? selectedOperationData.max_duplicates : (parseInt(selectedOperationData.max_duplicates, 10) || 5),
        is_reference_enabled: selectedOperationData.is_reference_enabled !== undefined ? selectedOperationData.is_reference_enabled : true,
        workflows: selectedOperationData.allowed_workflow_codes || [],
        comment: '',
      });
      setDomainSearchQuery(domainValue);
      setSaveError(null);
      setSaveSuccess(false);
    }
  }, [selectedOperationData]);
  
  // Sync domainSearchQuery with editFormData.domain when it changes externally
  useEffect(() => {
    if (editFormData.domain !== domainSearchQuery && !domainDropdownOpen) {
      setDomainSearchQuery(editFormData.domain);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editFormData.domain]);
  
  // Filter domains based on search query
  const filteredDomains = uniqueDomains.filter(domain =>
    domain.toLowerCase().includes(domainSearchQuery.toLowerCase())
  );
  
  // Check if current input is a new domain (not in existing list)
  const isNewDomain = domainSearchQuery && 
    !uniqueDomains.some(d => d.toLowerCase() === domainSearchQuery.toLowerCase());

  // Handle time input formatting for HH:MM (unlimited hours)
  const handleTimeInputChange = (value, setter) => {
    // Remove any non-digit characters except colon
    let cleaned = value.replace(/[^\d:]/g, '');
    
    // Remove extra colons (keep only the first one)
    const colonCount = (cleaned.match(/:/g) || []).length;
    if (colonCount > 1) {
      const parts = cleaned.split(':');
      cleaned = parts[0] + ':' + parts.slice(1).join('');
    }
    
    // Auto-insert colon after at least 1 digit if not present and user has typed more
    if (cleaned.length >= 1 && !cleaned.includes(':') && cleaned.length <= 10) {
      // Allow user to type hours first, then auto-insert colon
      // But don't force it - let them type naturally
    }
    
    // Prevent invalid minute values (00-59)
    if (cleaned.includes(':')) {
      const parts = cleaned.split(':');
      if (parts.length > 1 && parts[1]) {
        const minutes = parts[1].substring(0, 2);
        const minutesNum = parseInt(minutes, 10);
        if (minutesNum > 59) {
          cleaned = parts[0] + ':59';
        } else if (minutes.length > 2) {
          // Limit minutes to 2 digits
          cleaned = parts[0] + ':' + minutes.substring(0, 2);
        }
      }
    }
    
    setter(cleaned);
  };

  // MULTI-DOMAINE must always appear in Domain options when adding or editing an operation
  const DOMAIN_MULTI_DOMAINE = 'MULTI-DOMAINE';

  // Fetch all unique domains, production lines, and production types (without filters)
  const fetchAllDomains = async () => {
    try {
      // Fetch all operations without filters to get all unique domains, production lines, and production types
      const response = await api.getOperations({}, 1, 1000); // Large page size to get all
      const allOperations = response.results || [];
      const domainSet = new Set(allOperations.map(op => op.domain).filter(Boolean));
      domainSet.add(DOMAIN_MULTI_DOMAINE);
      const domains = [...domainSet].sort();
      const productionLines = [...new Set(allOperations.map(op => op.production_line_lp).filter(Boolean))].sort();
      const productionTypes = [...new Set(allOperations.map(op => op.production_type).filter(Boolean))].sort();
      setUniqueDomains(domains);
      setUniqueProductionLines(productionLines);
      setUniqueProductionTypes(productionTypes);
    } catch (error) {
      console.error('Error fetching domains:', error);
      // Don't show error to user, just log it
    }
  };

  // Fetch operations from backend
  const fetchOperations = async () => {
    setLoadingOperations(true);
    setOperationsError(null);
    try {
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (searchQuery) filters.search = searchQuery;
      if (domainFilter) filters.domain = domainFilter;
      if (productionLineFilter) filters.production_line_lp = productionLineFilter;

      const response = await api.getOperations(filters, currentPage, pageSize);
      const operationsArray = response.results || [];
      setOperations(operationsArray);
      setOperationsCount(response.count || 0);
      
      // Clear selection if selected operation is not in current page results
      if (selectedOperation && !operationsArray.find(op => op.id === selectedOperation)) {
        setSelectedOperation(null);
      }
    } catch (error) {
      const formattedError = formatApiError(error);
      setOperationsError(formattedError.generalError || t('operations.loadFailed'));
      console.error('Error fetching operations:', error);
    } finally {
      setLoadingOperations(false);
    }
  };

  const openDeleteOperationModal = async (operationId = null) => {
    const operationToDelete = operationId ?? selectedOperation;
    if (!operationToDelete) return;
    if (operationId && selectedOperation !== operationId) {
      setSelectedOperation(operationId);
    }
    setDeleteModalError(null);
    setOperationDeleteInfo(null);
    setShowDeleteOperationModal(true);
    setDeleteInfoLoading(true);
    setDeleteTargetOperationId('');
    try {
      const info = await getOperationDeleteInfo(operationToDelete);
      setOperationDeleteInfo(info);
      setDeleteRelatedMode((info?.imputation_count || 0) > 0 ? 'reassign' : 'delete');
    } catch (err) {
      const fe = formatApiError(err);
      setDeleteModalError(fe.generalError || t('operations.deleteInfoFailed'));
    } finally {
      setDeleteInfoLoading(false);
    }
  };

  const handleConfirmDeleteOperation = async () => {
    if (!selectedOperation) return;
    const count = operationDeleteInfo?.imputation_count ?? 0;
    const otherOps = operations.filter((o) => o.id !== selectedOperation);
    if (count > 0 && deleteRelatedMode === 'reassign') {
      const tid = parseInt(String(deleteTargetOperationId), 10);
      if (!tid) {
        setDeleteModalError(t('operations.selectReassignTarget'));
        return;
      }
      if (otherOps.length === 0) {
        setDeleteModalError(t('operations.noOtherOperationToReassign'));
        return;
      }
    }
    setDeleteSubmitLoading(true);
    setDeleteModalError(null);
    try {
      let payload = null;
      if (count > 0) {
        if (deleteRelatedMode === 'reassign') {
          payload = {
            related_entries: 'reassign',
            target_operation_id: parseInt(String(deleteTargetOperationId), 10),
          };
        } else {
          payload = { related_entries: 'delete' };
        }
      }
      await deleteOperation(selectedOperation, payload);
      setShowDeleteOperationModal(false);
      setSelectedOperation(null);
      setOperationDeleteInfo(null);
      await fetchOperations();
      await fetchAllDomains();
    } catch (err) {
      const fe = formatApiError(err);
      setDeleteModalError(fe.generalError || t('operations.deleteFailed'));
    } finally {
      setDeleteSubmitLoading(false);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, searchQuery, domainFilter, productionLineFilter]);

  // Fetch all domains on component mount
  useEffect(() => {
    fetchAllDomains();
  }, []);

  // Fetch workflows on mount for operation add/edit forms
  useEffect(() => {
    const loadWorkflows = async () => {
      try {
        const data = await getWorkflows();
        const list = Array.isArray(data) ? data : (data?.results ?? []);
        setAvailableWorkflows(list);
      } catch {
        setAvailableWorkflows([]);
      }
    };
    loadWorkflows();
  }, []);

  // Fetch user role on component mount
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const userData = await getCurrentUserProfile();
        setUserRole(userData?.role || null);
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };
    fetchUserRole();
  }, []);

  // Fetch operations when filters, page, or page size changes
  useEffect(() => {
    fetchOperations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, statusFilter, searchQuery, domainFilter, productionLineFilter]);

  // Handle Escape key to close modals
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (showDeleteOperationModal) {
          setShowDeleteOperationModal(false);
          return;
        }
        if (selectedOperation) {
          setSelectedOperation(null);
        }
        if (showAddModal) {
          setShowAddModal(false);
        }
      }
    };

    if (selectedOperation || showAddModal || showDeleteOperationModal) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [selectedOperation, showAddModal, showDeleteOperationModal]);

  // Reset add form when modal closes
  useEffect(() => {
    if (!showAddModal) {
      setAddFormData({
        production_line_lp: '',
        activity: '',
        domain: '',
        program: '',
        production_line_lp: '',
        production_type: '',
        description: '',
        temps_standard: '',
        is_active: true,
        allows_multiple_insertion: true,
        max_duplicates: 5,
        is_reference_enabled: true,
        workflows: [],
        comment: '',
      });
      setAddDomainSearchQuery('');
      setAddError(null);
      setAddSuccess(false);
    }
  }, [showAddModal]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setDomainFilter('');
    setProductionLineFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleFormChange = (field, value) => {
    setEditFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      return newData;
    });
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleAddFormChange = (field, value) => {
    setAddFormData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };
      return newData;
    });
    setAddError(null);
    setAddSuccess(false);
  };

  const handleAddOperation = async () => {
    // Validate required fields
    if (!isRsTenant && !addFormData.production_line_lp) {
      setAddError(t('operations.productionLineRequired'));
      return;
    }
    if (!addFormData.activity) {
      setAddError(t('operations.activityRequired'));
      return;
    }
    if (!addFormData.domain) {
      setAddError(t('operations.domainRequired'));
      return;
    }
    if (!addFormData.temps_standard) {
      setAddError(t('operations.standardTimeRequired'));
      return;
    }

    // Validate HH:MM format
    if (!addFormData.temps_standard || addFormData.temps_standard.trim() === '') {
      setAddError(t('operations.standardTimeRequired'));
      return;
    }
    
    const validation = validateHHMM(addFormData.temps_standard);
    if (!validation.isValid) {
      setAddError(t('operations.standardTimeFormat'));
      return;
    }

    // Convert HH:MM to decimal
    let tempsStandard;
    try {
      tempsStandard = hhmmToDecimal(addFormData.temps_standard);
      if (tempsStandard < 0) {
        setAddError(t('operations.standardTimeNonNegative'));
        return;
      }
    } catch (error) {
      setAddError(t('operations.invalidTimeFormat'));
      return;
    }

    // Validate max duplicates only when multiple insertion is allowed
    let addMaxDup = 1;
    if (addFormData.allows_multiple_insertion) {
      const parsedMaxDup = parseInt(addFormData.max_duplicates, 10);
      if (isNaN(parsedMaxDup) || parsedMaxDup < 1 || parsedMaxDup > 999) {
        setAddError(t('operations.maxDuplicatesRange'));
        return;
      }
      addMaxDup = parsedMaxDup;
    }

    setAdding(true);
    setAddError(null);
    setAddSuccess(false);

    try {
      const maxDup = Math.min(999, Math.max(1, addMaxDup));
      const createData = {
        production_line_lp: addFormData.production_line_lp,
        activity: addFormData.activity,
        domain: addFormData.domain,
        program: addFormData.program || '',
        production_line_lp: addFormData.production_line_lp || '',
        production_type: addFormData.production_type || '',
        description: addFormData.description || '',
        temps_standard: tempsStandard,
        is_active: addFormData.is_active,
        allows_multiple_insertion: addFormData.allows_multiple_insertion,
        max_duplicates: maxDup,
        is_reference_enabled: addFormData.is_reference_enabled,
        workflows: addFormData.workflows || [],
      };

      const response = await api.createOperation(createData);
      
      // Add the new operation to the local state
      setOperations(prevOperations => {
        const newOperations = [...prevOperations, response];
        // Sort by production line (LP) to maintain order
        return newOperations.sort((a, b) => (a.production_line_lp || '').localeCompare(b.production_line_lp || ''));
      });
      setOperationsCount(prevCount => prevCount + 1);
      
      // Refresh domains list to include new domain
      await fetchAllDomains();
      
      // Close modal and reset form
      setShowAddModal(false);
      setAddFormData({
        production_line_lp: '',
        activity: '',
        domain: '',
        program: '',
        production_line_lp: '',
        production_type: '',
        description: '',
        temps_standard: '',
        is_active: true,
        allows_multiple_insertion: true,
        max_duplicates: 5,
        is_reference_enabled: true,
        workflows: [],
        comment: '',
      });
      setAddDomainSearchQuery('');
      setAddSuccess(true);
    } catch (error) {
      const formattedError = formatApiError(error);
      setAddError(formattedError.generalError || t('operations.createFailed'));
      console.error('Error creating operation:', error);
    } finally {
      setAdding(false);
    }
  };

  const handleSaveOperation = async () => {
    if (!selectedOperation) return;

    // Validate required fields
    if (!editFormData.activity) {
      setSaveError(t('operations.activityRequired'));
      return;
    }
    if (!editFormData.domain) {
      setSaveError(t('operations.domainRequired'));
      return;
    }
    if (!editFormData.temps_standard) {
      setSaveError(t('operations.standardTimeRequired'));
      return;
    }

    // Validate HH:MM format
    if (!editFormData.temps_standard || editFormData.temps_standard.trim() === '') {
      setSaveError(t('operations.standardTimeRequired'));
      return;
    }
    
    const validation = validateHHMM(editFormData.temps_standard);
    if (!validation.isValid) {
      setSaveError(t('operations.standardTimeFormat'));
      return;
    }

    // Convert HH:MM to decimal
    let tempsStandard;
    try {
      tempsStandard = hhmmToDecimal(editFormData.temps_standard);
      if (tempsStandard < 0) {
        setSaveError(t('operations.standardTimeNonNegative'));
        return;
      }
    } catch (error) {
      setSaveError(t('operations.invalidTimeFormat'));
      return;
    }

    // Validate max duplicates only when multiple insertion is allowed
    let editMaxDup = 1;
    if (editFormData.allows_multiple_insertion) {
      const parsedMaxDup = parseInt(editFormData.max_duplicates, 10);
      if (isNaN(parsedMaxDup) || parsedMaxDup < 1 || parsedMaxDup > 999) {
        setSaveError(t('operations.maxDuplicatesRange'));
        return;
      }
      editMaxDup = parsedMaxDup;
    }

    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);

    try {
      // If user is a manager, send activity, domain, description, temps_standard, allows_multiple_insertion, max_duplicates
      // If user is admin, send all fields
      const maxDup = Math.min(999, Math.max(1, editMaxDup));
      const isManager = userRole === 'MANAGER';
      const baseUpdate = {
        production_line_lp: editFormData.production_line_lp,
        activity: editFormData.activity,
        domain: editFormData.domain,
        program: editFormData.program || '',
        production_line_lp: editFormData.production_line_lp || '',
        production_type: editFormData.production_type || '',
        description: editFormData.description || '',
        temps_standard: tempsStandard,
        allows_multiple_insertion: editFormData.allows_multiple_insertion,
        max_duplicates: maxDup,
        is_reference_enabled: editFormData.is_reference_enabled,
        workflows: editFormData.workflows || [],
        comment: editFormData.comment || '',
      };
      const updateData = isManager ? baseUpdate : { ...baseUpdate, production_line_lp: editFormData.production_line_lp };

      const response = await api.updateOperation(selectedOperation, updateData);
      
      // Update the operation in the local state
      setOperations(prevOperations => 
        prevOperations.map(op => {
          if (op.id === selectedOperation) {
            return { ...op, ...response };
          }
          return op;
        })
      );
      
      // Refresh domains list in case domain changed
      await fetchAllDomains();
      
      // Close the selected operation panel
      setSelectedOperation(null);
      setSaveSuccess(true);
    } catch (error) {
      const formattedError = formatApiError(error);
      setSaveError(formattedError.generalError || t('operations.saveFailed'));
      console.error('Error updating operation:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOperationStatus = async (operationId, currentStatus, e) => {
    e.stopPropagation();
    const newStatus = !currentStatus;
    
    // Optimistically update the UI
    setOperations(prevOperations => 
      prevOperations.map(op => {
        if (op.id === operationId) {
          return { ...op, is_active: newStatus };
        }
        return op;
      })
    );

    try {
      await api.toggleOperationStatus(operationId, newStatus);
      // No need to reload - optimistic update is sufficient
    } catch (error) {
      // Revert on error
      setOperations(prevOperations => 
        prevOperations.map(op => {
          if (op.id === operationId) {
            return { ...op, is_active: currentStatus };
          }
          return op;
        })
      );
      const formattedError = formatApiError(error);
      alert(formattedError.generalError || t('operations.statusUpdateFailed'));
      console.error('Error toggling operation status:', error);
    }
  };

  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, operationsCount);
  const totalPages = Math.ceil(operationsCount / pageSize);

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  const getStatusBadgeClass = (isActive) => {
    return isActive
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-200 dark:border-emerald-700/70'
      : 'bg-red-50 text-red-700 border-red-100 dark:bg-red-900/40 dark:text-red-200 dark:border-red-700/70';
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Page Heading */}
      <div className="flex flex-wrap justify-between items-end gap-4 px-1">
        <div className="flex min-w-72 flex-col gap-2">
          <h1 className="text-slate-900 text-3xl font-bold leading-tight tracking-[-0.02em]">
            {t('operations.title')}
          </h1>
          <p className="text-slate-500 text-base font-normal leading-normal">
            {t('operations.subtitle')}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => onNavigate && onNavigate('operation-logs')}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white text-slate-700 text-sm font-semibold border border-slate-200 shadow-sm hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">history</span>
            <span>{t('operations.viewLogs')}</span>
          </button>
          <button
            onClick={() => setShowComments((v) => !v)}
            className={`flex items-center justify-center gap-2 rounded-lg h-10 px-4 text-sm font-semibold border shadow-sm transition-colors ${
              showComments
                ? 'bg-primary/10 text-primary border-primary/30 hover:bg-primary/20 dark:bg-primary/20 dark:border-primary/50 dark:hover:bg-primary/30'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700'
            }`}
            title={showComments ? t('operations.hideCommentsTitle') : t('operations.showCommentsTitle')}
          >
            <span className="material-symbols-outlined text-[20px]">comment</span>
            <span>{showComments ? t('operations.hideComments') : t('operations.showComments')}</span>
          </button>
          {userRole === 'ADMIN' && (
          <button
            onClick={async () => {
              setShowManageWorkflowsModal(true);
              setNewWorkflowForm({ code: '', project_code: '', digits_in_project_code: 7, allow_alphabetic_chars: true });
              setWorkflowAddError(null);
              setEditingWorkflowId(null);
              setWorkflowToDelete(null);
              try {
                const data = await getWorkflows();
                const list = Array.isArray(data) ? data : (data?.results ?? []);
                setWorkflowsList(list);
                setAvailableWorkflows(list);
              } catch {
                setWorkflowsList([]);
              }
            }}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-white text-slate-700 text-sm font-semibold border border-slate-200 shadow-sm hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">account_tree</span>
            <span>{t('operations.manageWorkflows')}</span>
          </button>
          )}
          {userRole === 'ADMIN' && (
          <button
            onClick={() => {
              setAddFormData({
                production_line_lp: '',
                activity: '',
                domain: '',
                program: '',
                production_line_lp: '',
                production_type: '',
                description: '',
                temps_standard: '',
                is_active: true,
                allows_multiple_insertion: true,
                max_duplicates: 5,
                is_reference_enabled: true,
                workflows: [],
                comment: '',
              });
              setAddDomainSearchQuery('');
              setAddError(null);
              setAddSuccess(false);
              setShowAddModal(true);
            }}
            className="flex items-center justify-center gap-2 rounded-lg h-10 px-4 bg-primary text-white text-sm font-bold shadow-md hover:bg-primary-hover transition-colors"
          >
            <span className="material-symbols-outlined text-[20px]">add</span>
            <span>{t('operations.addNewOperation')}</span>
          </button>
          )}
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm flex flex-col gap-4">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex flex-col min-w-64 flex-1">
            <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider pb-2">
              {t('operations.searchLabel')}
            </p>
            <div className="relative">
              <input
                className="form-input w-full rounded-lg text-slate-900 focus:outline-0 focus:ring-2 focus:ring-primary/50 border border-slate-200 bg-slate-50 h-10 placeholder:text-slate-400 pl-10 pr-4 text-sm"
                placeholder={isRsTenant ? t('operations.searchPlaceholderRs') : t('operations.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-[20px]">search</span>
            </div>
          </label>
          <div className="flex gap-3 flex-wrap items-end">
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('operations.domain')}</p>
              <div className="relative">
                <select
                  value={domainFilter}
                  onChange={(e) => setDomainFilter(e.target.value)}
                  className="h-10 appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 min-w-[140px] text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
                >
                  <option value="">{t('operations.filterAllDomains')}</option>
                  {uniqueDomains.map((domain) => (
                    <option key={domain} value={domain}>
                      {domain}
                    </option>
                  ))}
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
            {!isRsTenant && (
              <div className="flex flex-col gap-2">
                <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('operations.productionLinePl')}</p>
                <div className="relative">
                  <select
                    value={productionLineFilter}
                    onChange={(e) => setProductionLineFilter(e.target.value)}
                    className="h-10 appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 min-w-[140px] text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
                  >
                    <option value="">{t('operations.filterAllLines')}</option>
                    {uniqueProductionLines.map((pl) => (
                      <option key={pl} value={pl}>
                        {pl}
                      </option>
                    ))}
                  </select>
                  <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
                </div>
              </div>
            )}
            <div className="flex flex-col gap-2">
              <p className="text-slate-700 text-xs font-semibold uppercase tracking-wider">{t('common:status')}</p>
              <div className="relative">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="h-10 appearance-none rounded-lg border border-slate-200 bg-white px-4 pr-10 min-w-[140px] text-slate-700 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary cursor-pointer"
                >
                  <option value="">{t('common:all')}</option>
                  <option value="active">{t('common:active')}</option>
                  <option value="inactive">{t('common:inactive')}</option>
                </select>
                <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-500 text-[20px] pointer-events-none">arrow_drop_down</span>
              </div>
            </div>
            <button
              onClick={handleClearFilters}
              className="h-10 px-4 text-sm font-medium text-primary hover:text-blue-700 transition-colors ml-2"
            >
              {t('common:clearAllFilters')}
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 gap-6 items-start">
        {/* Operations Table */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-lg font-bold text-slate-900">{t('operations.catalogTitle')}</h3>
              <span className="text-xs font-medium bg-slate-100 text-slate-600 px-2 py-1 rounded dark:bg-slate-700 dark:text-slate-300">
                {t('operations.count', { count: operationsCount })}
              </span>
            </div>
            <div className="overflow-x-auto">
            {operationsError ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-red-500">{operationsError}</div>
              </div>
            ) : operations.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-slate-500">{t('operations.noOperations')}</div>
              </div>
            ) : (
              <table className="w-full text-left text-sm" style={{ tableLayout: 'auto' }}>
                <thead className="bg-slate-50 text-slate-500 border-b border-slate-100">
                  <tr>
                    {/* <th className="px-6 py-3 font-semibold">Code</th> */}
                    <th className="px-6 py-3 font-semibold">{t('operations.activity')}</th>
                    <th className="px-6 py-3 font-semibold">{t('operations.domain')}</th>
                    <th className="px-6 py-3 font-semibold">{t('operations.program')}</th>
                    {!isRsTenant && <th className="px-6 py-3 font-semibold">{t('operations.productionLineLp')}</th>}
                    {!isRsTenant && <th className="px-6 py-3 font-semibold">{t('operations.productionType')}</th>}
                    <th className="px-6 py-3 font-semibold">{t('operations.standardTime')}</th>
                    {/* <th className="px-6 py-3 font-semibold">Status</th> */}
                    <th className="px-6 py-3 font-semibold text-right">{t('common:actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {operations.map((operation) => (
                  <React.Fragment key={operation.id}>
                  <motion.tr
                    className={`transition-colors group border-l-4 ${
                      selectedOperation === operation.id
                        ? 'bg-primary/5'
                        : 'border-l-transparent'
                    } ${userRole === 'ADMIN' ? 'cursor-pointer hover:bg-slate-50' : ''}`}
                    onClick={() => {
                      if (userRole === 'ADMIN') {
                        setSelectedOperation(selectedOperation === operation.id ? null : operation.id);
                      }
                    }}
                  >
                    {/* <td className="px-6 py-4">
                      <p className="font-semibold text-slate-900">{operation.production_line_lp}</p>
                    </td> */}
                    <td className="px-6 py-4" style={{ maxWidth: '300px', width: '300px' }}>
                      <p className="font-medium text-slate-900 truncate" title={operation.activity}>
                        {operation.activity}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-slate-500">
                      {operation.domain || '—'}
                    </td>
                    <td className="px-6 py-4 text-slate-500">
                      {operation.program || '—'}
                    </td>
                    {!isRsTenant && (
                      <td className="px-6 py-4 text-slate-500">
                        {operation.production_line_lp || '—'}
                      </td>
                    )}
                    {!isRsTenant && (
                      <td className="px-6 py-4 text-slate-500">
                        {operation.production_type || '—'}
                      </td>
                    )}
                    <td className="px-6 py-4">
                      {operation.temps_standard ? (
                        <span
                          className="inline-flex items-center gap-2 rounded-lg bg-slate-100 px-2.5 py-1.5 font-mono text-sm font-medium text-slate-700"
                          title={(() => {
                            const hhmm = typeof operation.temps_standard === 'string'
                              ? operation.temps_standard
                              : decimalToHHMM(operation.temps_standard);
                            try {
                              const h = hhmmToDecimal(hhmm);
                              return `${hhmm} (${h} h)`;
                            } catch {
                              return hhmm;
                            }
                          })()}
                        >
                          <span className="material-symbols-outlined text-slate-500 text-[18px]">schedule</span>
                          {typeof operation.temps_standard === 'string'
                            ? operation.temps_standard
                            : decimalToHHMM(operation.temps_standard)}
                        </span>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                    {/* <td className="px-6 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(operation.is_active)}`}>
                        <span className={`h-1.5 w-1.5 rounded-full ${
                          operation.is_active ? 'bg-emerald-500' : 'bg-red-500'
                        }`}></span>
                        {operation.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td> */}
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2.5">
                        {/* Toggle Active/Inactive Button - Admin only */}
                        {userRole === 'ADMIN' && (
                        <button
                          onClick={(e) => handleToggleOperationStatus(operation.id, operation.is_active, e)}
                          className={`h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm ${
                            operation.is_active
                              ? 'bg-gradient-to-r from-emerald-50 to-emerald-100/50 text-emerald-700 border border-emerald-300/50 dark:from-emerald-900/40 dark:to-emerald-800/30 dark:text-emerald-200 dark:border-emerald-700/70'
                              : 'bg-gradient-to-r from-slate-100 to-slate-50 text-slate-600 border border-slate-300/50 dark:from-slate-700 dark:to-slate-800 dark:text-slate-300 dark:border-slate-600'
                          }`}
                          title={operation.is_active ? t('operations.deactivate') : t('operations.activate')}
                        >
                          <span 
                            className={`material-symbols-outlined text-[18px] ${
                              operation.is_active ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400'
                            }`}
                          >
                            {operation.is_active ? 'check_circle' : 'cancel'}
                          </span>
                          <span className="font-medium">{operation.is_active ? t('common:active') : t('common:inactive')}</span>
                        </button>
                        )}
                        {isRsTenant && userRole === 'ADMIN' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openDeleteOperationModal(operation.id);
                            }}
                            className="h-9 px-4 rounded-lg text-xs font-semibold flex items-center gap-2 shadow-sm border border-red-200 bg-red-50 text-red-800 hover:bg-red-100 transition-colors"
                            title={t('operations.deleteOperation')}
                          >
                            <span className="material-symbols-outlined text-[18px] text-red-700">delete</span>
                            <span className="font-medium">{t('common:delete')}</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                  {showComments && operation.description && (
                    <tr
                      className={`bg-slate-50/70 ${userRole === 'ADMIN' ? 'hover:bg-slate-50 cursor-pointer' : ''}`}
                      onClick={() => userRole === 'ADMIN' && setSelectedOperation(selectedOperation === operation.id ? null : operation.id)}
                    >
                      <td colSpan={isRsTenant ? 5 : 7} className="px-6 py-2 text-sm text-slate-600 border-t-0 align-top cursor-pointer">
                        <span className="font-medium text-slate-500 mr-2">{t('operations.commentLabel')}</span>
                        {operation.description}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  ))}
                </tbody>
              </table>
            )}
            </div>
            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
              <div className="flex items-center gap-4">
                <span className="text-sm text-slate-500">
                  {t('common:showingRange', { from: operationsCount > 0 ? startIndex : 0, to: endIndex, total: operationsCount })}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">{t('common:perPage')}</span>
                  <div className="relative">
                    <select
                      value={pageSize}
                      onChange={(e) => setPageSize(Number(e.target.value))}
                      className="appearance-none rounded border border-slate-200 bg-white px-3 py-1.5 pr-8 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="material-symbols-outlined absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 text-[16px] pointer-events-none">arrow_drop_down</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:firstPage')}
                >
                  <span className="material-symbols-outlined text-sm">first_page</span>
                </button>
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:previousPage')}
                >
                  <span className="material-symbols-outlined text-sm">chevron_left</span>
                </button>
                
                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`h-8 w-8 flex items-center justify-center rounded border text-sm font-medium transition-colors ${
                          currentPage === page
                            ? 'bg-primary text-white border-primary'
                            : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700'
                        }`}
                      >
                        {page}
                      </button>
                    )
                  ))}
                </div>
                
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:nextPage')}
                >
                  <span className="material-symbols-outlined text-sm">chevron_right</span>
                </button>
                <button
                  onClick={() => handlePageChange(totalPages)}
                  disabled={currentPage >= totalPages}
                  className="h-8 w-8 flex items-center justify-center rounded border border-slate-200 bg-white text-slate-500 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title={t('common:lastPage')}
                >
                  <span className="material-symbols-outlined text-sm">last_page</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Operation Edit Modal */}
      <AnimatePresence>
        {selectedOperation && selectedOperationData && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setSelectedOperation(null)}
            />
            {/* Modal */}
            <div onClick={() => setSelectedOperation(null)} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200 my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setSelectedOperation(null);
                  }
                }}
              >
                <div 
                  className="min-h-full"
                  onClick={(e) => e.stopPropagation()}
                >
                <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10 relative">
                  <div className="flex-1 pr-12">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('operations.selectedOperation')}</p>
                    <h3 className="text-xl font-bold text-slate-900">
                      {isRsTenant ? selectedOperationData.activity : selectedOperationData.production_line_lp}
                    </h3>
                    <p className="text-sm text-slate-500">
                      {selectedOperationData.activity}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedOperation(null)}
                    className="absolute top-6 right-6 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
                    title={t('common:closeModal')}
                  >
                    <span className="material-symbols-outlined text-[22px] group-hover:scale-110 transition-transform duration-200">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-6">
              {/* Success/Error Messages */}
              {saveSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 text-sm">
                  {t('operations.updated')}
                </div>
              )}
              {saveError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                  {saveError}
                </div>
              )}

              {/* Operation Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isRsTenant && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('operations.productionLineLp')} {userRole !== 'MANAGER' && '*'}</label>
                    <input
                      type="text"
                      value={editFormData.production_line_lp}
                      onChange={(e) => handleFormChange('production_line_lp', e.target.value)}
                      disabled={userRole === 'MANAGER'}
                      className={`w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none ${
                        userRole === 'MANAGER' 
                          ? 'bg-slate-100 cursor-not-allowed opacity-75' 
                          : 'bg-slate-50'
                      }`}
                      placeholder={t('operations.productionLineLp')}
                      readOnly={userRole === 'MANAGER'}
                    />
                    {userRole === 'MANAGER' && (
                      <p className="text-xs text-slate-500">{t('operations.lpReadOnlyForManagers')}</p>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('operations.activity')} *</label>
                  <input
                    type="text"
                    value={editFormData.activity}
                    onChange={(e) => handleFormChange('activity', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    placeholder={t('operations.activityPlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('operations.domain')} *</label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        type="text"
                        value={domainSearchQuery}
                        onChange={(e) => {
                          setDomainSearchQuery(e.target.value);
                          handleFormChange('domain', e.target.value);
                          setDomainDropdownOpen(true);
                        }}
                        onFocus={() => setDomainDropdownOpen(true)}
                        onBlur={() => {
                          // Delay closing to allow click on dropdown items
                          setTimeout(() => setDomainDropdownOpen(false), 200);
                        }}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none pr-10"
                        placeholder={t('operations.domainPlaceholder')}
                      />
                      <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px] pointer-events-none">arrow_drop_down</span>
                    </div>
                    
                    {/* Custom Dropdown */}
                    <AnimatePresence>
                      {domainDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-60 overflow-auto"
                        >
                        {filteredDomains.length > 0 && (
                          <div className="p-2">
                            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1 mb-1">
                              {t('operations.existingDomains')}
                            </p>
                            {filteredDomains.map((domain) => (
                              <button
                                key={domain}
                                type="button"
                                onClick={() => {
                                  setDomainSearchQuery(domain);
                                  handleFormChange('domain', domain);
                                  setDomainDropdownOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-2"
                              >
                                <span className="material-symbols-outlined text-[18px] text-slate-400">folder</span>
                                <span>{domain}</span>
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {isNewDomain && (
                          <div className="border-t border-slate-200 p-2">
                            <button
                              type="button"
                              onClick={() => {
                                handleFormChange('domain', domainSearchQuery);
                                setDomainDropdownOpen(false);
                              }}
                              className="w-full text-left px-3 py-2 rounded-md text-sm text-primary hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
                            >
                              <span className="material-symbols-outlined text-[18px]">add_circle</span>
                              <span>{t('operations.createNamed', { name: domainSearchQuery })}</span>
                            </button>
                          </div>
                        )}
                        
                        {filteredDomains.length === 0 && !isNewDomain && domainSearchQuery && (
                          <div className="p-3 text-center text-sm text-slate-500">
                            {t('operations.noMatchingDomains')}
                          </div>
                        )}
                        
                        {!domainSearchQuery && uniqueDomains.length === 0 && (
                          <div className="p-3 text-center text-sm text-slate-500">
                            {t('operations.noExistingDomains')}
                          </div>
                        )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {isNewDomain ? (
                      <span className="text-primary font-medium">{t('operations.creatingDomain', { name: domainSearchQuery })}</span>
                    ) : (
                      t('operations.domainHint')
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('operations.program')}</label>
                  <input
                    type="text"
                    value={editFormData.program}
                    onChange={(e) => handleFormChange('program', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    placeholder={t('operations.programPlaceholder')}
                  />
                </div>

                {!isRsTenant && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('operations.productionType')}</label>
                    <div className="relative">
                      <div className="relative">
                        <input
                          type="text"
                          value={editFormData.production_type}
                          onChange={(e) => {
                            handleFormChange('production_type', e.target.value);
                            setProductionTypeDropdownOpen(true);
                          }}
                          onFocus={() => setProductionTypeDropdownOpen(true)}
                          onBlur={() => setTimeout(() => setProductionTypeDropdownOpen(false), 200)}
                          className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none pr-10"
                          placeholder={t('operations.productionTypePlaceholder')}
                        />
                        <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px] pointer-events-none">arrow_drop_down</span>
                      </div>
                      <AnimatePresence>
                        {productionTypeDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-60 overflow-auto"
                          >
                            {filteredProductionTypes.length > 0 && (
                              <div className="p-2">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1 mb-1">
                                  {t('operations.productionType')}
                                </p>
                                {filteredProductionTypes.map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    onClick={() => {
                                      handleFormChange('production_type', opt);
                                      setProductionTypeDropdownOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-2"
                                  >
                                    <span className="material-symbols-outlined text-[18px] text-slate-400">category</span>
                                    <span>{opt}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {isNewProductionType && (
                              <div className="border-t border-slate-200 p-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleFormChange('production_type', (editFormData.production_type || '').trim());
                                    setProductionTypeDropdownOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 rounded-md text-sm text-primary hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
                                >
                                  <span className="material-symbols-outlined text-[18px]">add_circle</span>
                                  <span>{t('operations.createNamed', { name: editFormData.production_type.trim() })}</span>
                                </button>
                              </div>
                            )}
                            {filteredProductionTypes.length === 0 && !isNewProductionType && editFormData.production_type && (
                              <div className="p-3 text-center text-sm text-slate-500">
                                {t('operations.noMatchingOption')}
                              </div>
                            )}
                            {!editFormData.production_type && (
                              <div className="p-3 text-center text-sm text-slate-500">
                                {uniqueProductionTypes.length === 0
                                  ? t('operations.noProductionTypesYet')
                                  : t('operations.selectOrTypeProductionType')}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {isNewProductionType ? (
                        <span className="text-primary font-medium">{t('operations.creatingProductionType', { name: editFormData.production_type.trim() })}</span>
                      ) : (
                        t('operations.productionTypeHint')
                      )}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    {/* <span className="material-symbols-outlined text-slate-500 text-[20px]">schedule</span> */}
                    {t('operations.standardTime')} <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col gap-3">
                    <StandardTimeSpinner
                      value={editFormData.temps_standard || '00:00'}
                      onChange={(hhmm) => {
                        handleFormChange('temps_standard', hhmm);
                        setSaveError(null);
                      }}
                      hasError={!!(saveError && (saveError.includes('Standard Time') || saveError.includes(t('operations.standardTime'))))}
                      ariaErrorId="edit-std-time-error"
                      ariaHintId="edit-std-time-hint"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-500">{t('operations.quickPresets')}</span>
                      {STANDARD_TIME_PRESETS.map((preset) => (
                        <button
                          key={preset.value}
                          type="button"
                          onClick={() => {
                            handleFormChange('temps_standard', preset.value);
                            setSaveError(null);
                          }}
                          className="px-2.5 py-1.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                        >
                          {t(preset.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p id="edit-std-time-hint" className="text-xs text-slate-500">
                    {t('operations.standardTimeHint')}
                  </p>
                  {saveError && (saveError.includes('Standard Time') || saveError.includes(t('operations.standardTime'))) && (
                    <p id="edit-std-time-error" className="text-xs text-red-600 flex items-center gap-1" role="alert">
                      <span className="material-symbols-outlined text-[14px]">error</span>
                      {saveError}
                    </p>
                  )}
                </div>

                {(userRole === 'MANAGER' || userRole === 'ADMIN') && (
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('operations.allowedWorkflows')}</label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{t('operations.allowedWorkflowsHint')}</p>
                  <div className="relative flex h-12 w-full items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800 p-1 border border-slate-200 dark:border-slate-600 gap-1">
                    {workflowCodesForDisplay.map((code) => {
                      const w = availableWorkflows.find(x => x.code === code);
                      const label = w?.label || code;
                      const isSelected = (editFormData.workflows || []).includes(code);
                      return (
                        <label key={code} className="flex-1 h-full cursor-pointer relative z-10">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const current = editFormData.workflows || [];
                              const next = isSelected
                                ? current.filter((c) => c !== code)
                                : [...current, code];
                              handleFormChange('workflows', next);
                            }}
                            className="sr-only"
                          />
                          <motion.div
                            className={`flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10 transition-colors duration-200 ${
                              isSelected
                                ? 'bg-white dark:bg-slate-700 text-primary dark:text-slate-200 shadow-sm dark:shadow-slate-900/50'
                                : 'bg-transparent text-slate-500 dark:text-slate-400'
                            }`}
                            transition={{ duration: 0.2 }}
                          >
                            {label}
                          </motion.div>
                        </label>
                      );
                    })}
                  </div>
                </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('operations.multipleInsertion')}</label>
                  <div className="relative flex h-12 w-full items-center justify-center rounded-lg bg-slate-50 p-1 border border-slate-200">
                    <motion.div
                      className="absolute h-[calc(100%-0.5rem)] rounded-md bg-white shadow-sm"
                      initial={false}
                      animate={{
                        x: editFormData.allows_multiple_insertion ? 0 : '100%',
                        width: 'calc(50% - 0.25rem)'
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30
                      }}
                      style={{
                        left: '0.125rem'
                      }}
                    />
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="allows_multiple_insertion_edit"
                        checked={editFormData.allows_multiple_insertion === true}
                        onChange={() => handleFormChange('allows_multiple_insertion', true)}
                        className="sr-only"
                      />
                      <motion.div
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: editFormData.allows_multiple_insertion ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('operations.allowed')}
                      </motion.div>
                    </label>
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="allows_multiple_insertion_edit"
                        checked={editFormData.allows_multiple_insertion === false}
                        onChange={() => handleFormChange('allows_multiple_insertion', false)}
                        className="sr-only"
                      />
                      <motion.div
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: editFormData.allows_multiple_insertion === false ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('operations.denied')}
                      </motion.div>
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">
                    {t('operations.multipleInsertionHint')}
                  </p>
                </div>

                {editFormData.allows_multiple_insertion && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('operations.maxDuplicates')}</label>
                    <input
                      type="number"
                      min={1}
                      max={999}
                      value={editFormData.max_duplicates}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v) && v >= 1 && v <= 999) handleFormChange('max_duplicates', v);
                        else if (e.target.value === '') handleFormChange('max_duplicates', 5);
                      }}
                      className="w-full h-12 rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                    <p className="text-xs text-slate-500">
                      {t('operations.maxDuplicatesHint')}
                    </p>
                  </div>
                )}



                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('common:description')}</label>
                  <textarea
                    value={editFormData.description}
                    onChange={(e) => handleFormChange('description', e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-slate-50"
                    placeholder={t('operations.descriptionPlaceholder')}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">
                    {t('operations.comment')} <span className="text-slate-400 font-normal text-xs">{t('operations.commentOptionalHint')}</span>
                  </label>
                  <textarea
                    value={editFormData.comment}
                    onChange={(e) => handleFormChange('comment', e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-slate-200 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none bg-slate-50"
                    placeholder={t('operations.commentPlaceholder')}
                  />
                </div>
              </div>

              {/* Save Button */}
              <div className="pt-4 border-t border-slate-100 space-y-3">
                {isRsTenant && userRole === 'ADMIN' && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      openDeleteOperationModal();
                    }}
                    className="w-full h-10 rounded-lg border border-red-200 bg-red-50 text-red-800 font-semibold text-sm hover:bg-red-100 transition-colors"
                  >
                    {t('operations.deleteOperation')}
                  </button>
                )}
                <button
                  onClick={handleSaveOperation}
                  disabled={saving}
                  className="w-full h-10 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? t('common:saving') : t('operations.saveChanges')}
                </button>
              </div>
                </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Add Operation Modal */}
      <AnimatePresence>
        {showAddModal && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowAddModal(false)}
            />
            {/* Modal */}
            <div onClick={() => setShowAddModal(false)} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white rounded-xl shadow-xl max-w-2xl w-full border border-slate-200 my-8 max-h-[90vh] overflow-y-auto"
                onClick={(e) => {
                  if (e.target === e.currentTarget) {
                    setShowAddModal(false);
                  }
                }}
              >
                <div 
                  className="min-h-full"
                  onClick={(e) => e.stopPropagation()}
                >
                <div className="p-6 border-b border-slate-100 flex justify-between items-start sticky top-0 bg-white z-10 relative">
                  <div className="flex-1 pr-12">
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('operations.addNewOperation')}</p>
                    <h3 className="text-xl font-bold text-slate-900">{t('operations.createOperation')}</h3>
                    <p className="text-sm text-slate-500">
                      {t('operations.createSubtitle')}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowAddModal(false)}
                    className="absolute top-6 right-6 h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-primary/20 focus:ring-offset-1"
                    title={t('common:closeModal')}
                  >
                    <span className="material-symbols-outlined text-[22px] group-hover:scale-110 transition-transform duration-200">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-6">
              {/* Success/Error Messages */}
              {addSuccess && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700/50 dark:text-emerald-200 text-sm">
                  {t('operations.created')}
                </div>
              )}
              {addError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 dark:bg-red-900/30 dark:border-red-700/50 dark:text-red-200 text-sm">
                  {addError}
                </div>
              )}

              {/* Operation Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isRsTenant && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('operations.productionLineLp')} *</label>
                    <input
                      type="text"
                      value={addFormData.production_line_lp}
                      onChange={(e) => handleAddFormChange('production_line_lp', e.target.value)}
                      className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                      placeholder={t('operations.productionLineLp')}
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('operations.activity')} *</label>
                  <input
                    type="text"
                    value={addFormData.activity}
                    onChange={(e) => handleAddFormChange('activity', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    placeholder={t('operations.activityPlaceholder')}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('operations.domain')} *</label>
                  <div className="relative">
                    <div className="relative">
                      <input
                        type="text"
                        value={addDomainSearchQuery}
                        onChange={(e) => {
                          setAddDomainSearchQuery(e.target.value);
                          handleAddFormChange('domain', e.target.value);
                          setAddDomainDropdownOpen(true);
                        }}
                        onFocus={() => setAddDomainDropdownOpen(true)}
                        onBlur={() => {
                          setTimeout(() => setAddDomainDropdownOpen(false), 200);
                        }}
                        className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none pr-10"
                        placeholder={t('operations.domainPlaceholder')}
                      />
                      <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px] pointer-events-none">arrow_drop_down</span>
                    </div>
                    
                    {/* Custom Dropdown */}
                    <AnimatePresence>
                      {addDomainDropdownOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -10 }}
                          transition={{ duration: 0.2 }}
                          className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-60 overflow-auto"
                        >
                          {filteredAddDomains.length > 0 && (
                            <div className="p-2">
                              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1 mb-1">
                                {t('operations.existingDomains')}
                              </p>
                              {filteredAddDomains.map((domain) => (
                                <button
                                  key={domain}
                                  type="button"
                                  onClick={() => {
                                    setAddDomainSearchQuery(domain);
                                    handleAddFormChange('domain', domain);
                                    setAddDomainDropdownOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-2"
                                >
                                  <span className="material-symbols-outlined text-[18px] text-slate-400">folder</span>
                                  <span>{domain}</span>
                                </button>
                              ))}
                            </div>
                          )}
                          
                          {isNewAddDomain && (
                            <div className="border-t border-slate-200 p-2">
                              <button
                                type="button"
                                onClick={() => {
                                  handleAddFormChange('domain', addDomainSearchQuery);
                                  setAddDomainDropdownOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 rounded-md text-sm text-primary hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
                              >
                                <span className="material-symbols-outlined text-[18px]">add_circle</span>
                                <span>{t('operations.createNamed', { name: addDomainSearchQuery })}</span>
                              </button>
                            </div>
                          )}
                          
                          {filteredAddDomains.length === 0 && !isNewAddDomain && addDomainSearchQuery && (
                            <div className="p-3 text-center text-sm text-slate-500">
                              {t('operations.noMatchingDomains')}
                            </div>
                          )}
                          
                          {!addDomainSearchQuery && uniqueDomains.length === 0 && (
                            <div className="p-3 text-center text-sm text-slate-500">
                              {t('operations.noExistingDomains')}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {isNewAddDomain ? (
                      <span className="text-primary font-medium">{t('operations.creatingDomain', { name: addDomainSearchQuery })}</span>
                    ) : (
                      t('operations.domainHint')
                    )}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('operations.program')}</label>
                  <input
                    type="text"
                    value={addFormData.program}
                    onChange={(e) => handleAddFormChange('program', e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    placeholder={t('operations.programPlaceholder')}
                  />
                </div>

                {!isRsTenant && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('operations.productionType')}</label>
                    <div className="relative">
                      <div className="relative">
                        <input
                          type="text"
                          value={addFormData.production_type}
                          onChange={(e) => {
                            handleAddFormChange('production_type', e.target.value);
                            setAddProductionTypeDropdownOpen(true);
                          }}
                          onFocus={() => setAddProductionTypeDropdownOpen(true)}
                          onBlur={() => setTimeout(() => setAddProductionTypeDropdownOpen(false), 200)}
                          className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none pr-10"
                          placeholder={t('operations.productionTypePlaceholder')}
                        />
                        <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-[20px] pointer-events-none">arrow_drop_down</span>
                      </div>
                      <AnimatePresence>
                        {addProductionTypeDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.2 }}
                            className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg max-h-60 overflow-auto"
                          >
                            {filteredAddProductionTypes.length > 0 && (
                              <div className="p-2">
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1 mb-1">
                                  {t('operations.productionType')}
                                </p>
                                {filteredAddProductionTypes.map((opt) => (
                                  <button
                                    key={opt}
                                    type="button"
                                    onClick={() => {
                                      handleAddFormChange('production_type', opt);
                                      setAddProductionTypeDropdownOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2 rounded-md text-sm text-slate-700 hover:bg-slate-100 transition-colors flex items-center gap-2"
                                  >
                                    <span className="material-symbols-outlined text-[18px] text-slate-400">category</span>
                                    <span>{opt}</span>
                                  </button>
                                ))}
                              </div>
                            )}
                            {isNewAddProductionType && (
                              <div className="border-t border-slate-200 p-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleAddFormChange('production_type', (addFormData.production_type || '').trim());
                                    setAddProductionTypeDropdownOpen(false);
                                  }}
                                  className="w-full text-left px-3 py-2 rounded-md text-sm text-primary hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
                                >
                                  <span className="material-symbols-outlined text-[18px]">add_circle</span>
                                  <span>{t('operations.createNamed', { name: addFormData.production_type.trim() })}</span>
                                </button>
                              </div>
                            )}
                            {filteredAddProductionTypes.length === 0 && !isNewAddProductionType && addFormData.production_type && (
                              <div className="p-3 text-center text-sm text-slate-500">
                                {t('operations.noMatchingOption')}
                              </div>
                            )}
                            {!addFormData.production_type && (
                              <div className="p-3 text-center text-sm text-slate-500">
                                {uniqueProductionTypes.length === 0
                                  ? t('operations.noProductionTypesYet')
                                  : t('operations.selectOrTypeProductionType')}
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {isNewAddProductionType ? (
                        <span className="text-primary font-medium">{t('operations.creatingProductionType', { name: addFormData.production_type.trim() })}</span>
                      ) : (
                        t('operations.productionTypeHint')
                      )}
                    </p>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 flex items-center gap-2">
                    {/* <span className="material-symbols-outlined text-slate-500 text-[20px]">schedule</span> */}
                    {t('operations.standardTime')} <span className="text-red-500">*</span>
                  </label>
                  <div className="flex flex-col gap-3">
                    <StandardTimeSpinner
                      value={addFormData.temps_standard || '00:00'}
                      onChange={(hhmm) => {
                        handleAddFormChange('temps_standard', hhmm);
                        setAddError(null);
                      }}
                      hasError={!!(addError && (addError.includes('Standard Time') || addError.includes(t('operations.standardTime'))))}
                      ariaErrorId="add-std-time-error"
                      ariaHintId="add-std-time-hint"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs text-slate-500">{t('operations.quickPresets')}</span>
                      {STANDARD_TIME_PRESETS.map((preset) => (
                        <button
                          key={preset.value}
                          type="button"
                          onClick={() => {
                            handleAddFormChange('temps_standard', preset.value);
                            setAddError(null);
                          }}
                          className="px-2.5 py-1.5 rounded-md text-xs font-medium bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                        >
                          {t(preset.labelKey)}
                        </button>
                      ))}
                    </div>
                  </div>
                  <p id="add-std-time-hint" className="text-xs text-slate-500">
                    {t('operations.standardTimeHint')}
                  </p>
                  {addError && (addError.includes('Standard Time') || addError.includes(t('operations.standardTime'))) && (
                    <p id="add-std-time-error" className="text-xs text-red-600 flex items-center gap-1" role="alert">
                      <span className="material-symbols-outlined text-[14px]">error</span>
                      {addError}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('common:status')}</label>
                  <div className="relative flex h-12 w-full items-center justify-center rounded-lg bg-slate-50 p-1 border border-slate-200">
                    <motion.div
                      className="absolute h-[calc(100%-0.5rem)] rounded-md bg-white shadow-sm"
                      initial={false}
                      animate={{
                        x: addFormData.is_active ? 0 : '100%',
                        width: 'calc(50% - 0.25rem)'
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30
                      }}
                      style={{
                        left: '0.125rem'
                      }}
                    />
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active_add"
                        checked={addFormData.is_active === true}
                        onChange={() => handleAddFormChange('is_active', true)}
                        className="sr-only"
                      />
                      <motion.div
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: addFormData.is_active ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:active')}
                      </motion.div>
                    </label>
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="is_active_add"
                        checked={addFormData.is_active === false}
                        onChange={() => handleAddFormChange('is_active', false)}
                        className="sr-only"
                      />
                      <motion.div
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: addFormData.is_active === false ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('common:inactive')}
                      </motion.div>
                    </label>
                  </div>
                </div>

                {(userRole === 'MANAGER' || userRole === 'ADMIN') && (
                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('operations.allowedWorkflows')}</label>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">{t('operations.allowedWorkflowsHint')}</p>
                  <div className="relative flex h-12 w-full items-center justify-center rounded-lg bg-slate-50 dark:bg-slate-800 p-1 border border-slate-200 dark:border-slate-600 gap-1">
                    {workflowCodesForDisplay.map((code) => {
                      const w = availableWorkflows.find(x => x.code === code);
                      const label = w?.label || code;
                      const isSelected = (addFormData.workflows || []).includes(code);
                      return (
                        <label key={code} className="flex-1 h-full cursor-pointer relative z-10">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => {
                              const current = addFormData.workflows || [];
                              const next = isSelected
                                ? current.filter((c) => c !== code)
                                : [...current, code];
                              handleAddFormChange('workflows', next);
                            }}
                            className="sr-only"
                          />
                          <motion.div
                            className={`flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10 transition-colors duration-200 ${
                              isSelected
                                ? 'bg-white dark:bg-slate-700 text-primary dark:text-slate-200 shadow-sm dark:shadow-slate-900/50'
                                : 'bg-transparent text-slate-500 dark:text-slate-400'
                            }`}
                            transition={{ duration: 0.2 }}
                          >
                            {label}
                          </motion.div>
                        </label>
                      );
                    })}
                  </div>
                </div>
                )}

                <div className="space-y-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('operations.multipleInsertion')}</label>
                  <div className="relative flex h-12 w-full items-center justify-center rounded-lg bg-slate-50 p-1 border border-slate-200">
                    <motion.div
                      className="absolute h-[calc(100%-0.5rem)] rounded-md bg-white shadow-sm"
                      initial={false}
                      animate={{
                        x: addFormData.allows_multiple_insertion ? 0 : '100%',
                        width: 'calc(50% - 0.25rem)'
                      }}
                      transition={{
                        type: 'spring',
                        stiffness: 300,
                        damping: 30
                      }}
                      style={{
                        left: '0.125rem'
                      }}
                    />
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="allows_multiple_insertion_add"
                        checked={addFormData.allows_multiple_insertion === true}
                        onChange={() => handleAddFormChange('allows_multiple_insertion', true)}
                        className="sr-only"
                      />
                      <motion.div
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: addFormData.allows_multiple_insertion ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('operations.allowed')}
                      </motion.div>
                    </label>
                    <label className="flex-1 cursor-pointer h-full relative z-10">
                      <input
                        type="radio"
                        name="allows_multiple_insertion_add"
                        checked={addFormData.allows_multiple_insertion === false}
                        onChange={() => handleAddFormChange('allows_multiple_insertion', false)}
                        className="sr-only"
                      />
                      <motion.div
                        className="flex h-full w-full items-center justify-center rounded-md text-sm font-medium relative z-10"
                        animate={{
                          color: addFormData.allows_multiple_insertion === false ? '#2563eb' : '#64748b'
                        }}
                        transition={{ duration: 0.2 }}
                      >
                        {t('operations.denied')}
                      </motion.div>
                    </label>
                  </div>
                  <p className="text-xs text-slate-500">
                    {t('operations.multipleInsertionHint')}
                  </p>
                </div>

                {addFormData.allows_multiple_insertion && (
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-900 block">{t('operations.maxDuplicates')}</label>
                    <input
                      type="number"
                      min={1}
                      max={999}
                      value={addFormData.max_duplicates}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        if (!isNaN(v) && v >= 1 && v <= 999) handleAddFormChange('max_duplicates', v);
                        else if (e.target.value === '') handleAddFormChange('max_duplicates', 5);
                      }}
                      className="w-full h-12 rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    />
                    <p className="text-xs text-slate-500">
                      {t('operations.maxDuplicatesHint')}
                    </p>
                  </div>
                )}


                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">{t('common:description')}</label>
                  <textarea
                    value={addFormData.description}
                    onChange={(e) => handleAddFormChange('description', e.target.value)}
                    rows={4}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    placeholder={t('operations.descriptionPlaceholder')}
                  />
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-sm font-semibold text-slate-900 block">
                    {t('operations.comment')} <span className="text-slate-400 font-normal text-xs">{t('operations.commentOptionalHint')}</span>
                  </label>
                  <textarea
                    value={addFormData.comment}
                    onChange={(e) => handleAddFormChange('comment', e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-900 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                    placeholder={t('operations.commentPlaceholder')}
                  />
                </div>
              </div>

              {/* Create Button */}
              <div className="pt-4 border-t border-slate-100">
                <button
                  onClick={handleAddOperation}
                  disabled={adding}
                  className="w-full h-10 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {adding ? t('operations.creating') : t('operations.createOperation')}
                </button>
              </div>
                </div>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Manage Workflows Modal */}
      <AnimatePresence>
        {showManageWorkflowsModal && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 bg-black/50 z-50"
              onClick={() => setShowManageWorkflowsModal(false)}
            />
            <div onClick={() => setShowManageWorkflowsModal(false)} className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ duration: 0.2 }}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-[54rem] w-full border border-slate-200 dark:border-slate-600 my-8"
                onClick={(e) => e.stopPropagation()}
              >
                <style>{`.toggle-switch {
  font-size: 10px;
  position: relative;
  display: inline-block;
  width: 3.5em;
  height: 2em;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.toggle-slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgb(182, 182, 182);
  transition: .4s;
  border-radius: 10px;
}
.toggle-slider:before {
  position: absolute;
  content: "";
  height: 1.4em;
  width: 1.4em;
  border-radius: 8px;
  left: 0.3em;
  bottom: 0.3em;
  background-color: rgb(255, 255, 255);
  transition: .4s;
}
.toggle-switch input:checked + .toggle-slider {
  background-color: ${isRsTenant ? '#2a2a2a' : '#124191'};
}
.toggle-switch input:focus + .toggle-slider {
  box-shadow: 0 0 1px #2196F3;
}
.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(1.5em);
}`}</style>
                <div className="p-6 border-b border-slate-100 dark:border-slate-600 flex justify-between items-start">
                  <div>
                    <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('operations.workflows')}</p>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">{t('operations.manageWorkflows')}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                      {t('operations.workflowsSubtitle')}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowManageWorkflowsModal(false)}
                    className="h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-all"
                    title={t('common:close')}
                  >
                    <span className="material-symbols-outlined text-[22px]">close</span>
                  </button>
                </div>
                <div className="p-6 space-y-6 flex flex-row gap-4">
                  {/* Add New Workflow Form */}
                  <div className="space-y-4 flex-1">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300">{t('operations.addNewWorkflow')}</p>
                    {workflowAddError && (
                      <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 text-red-700 dark:text-red-200 text-sm">
                        {workflowAddError}
                      </div>
                    )}
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('operations.workflowName')} *</label>
                        <input
                          type="text"
                          value={newWorkflowForm.code}
                          onChange={(e) => setNewWorkflowForm((f) => ({ ...f, code: e.target.value.trim().toUpperCase() }))}
                          placeholder={t('operations.workflowNamePlaceholder')}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('operations.projectCodeRelated')}</label>
                        <input
                          type="text"
                          value={newWorkflowForm.project_code}
                          onChange={(e) => setNewWorkflowForm((f) => ({ ...f, project_code: e.target.value }))}
                          placeholder={t('operations.projectCodePlaceholder')}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('operations.digitsInProjectCode')}</label>
                        <input
                          type="number"
                          min={0}
                          max={20}
                          value={newWorkflowForm.digits_in_project_code}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v >= 0 && v <= 20) setNewWorkflowForm((f) => ({ ...f, digits_in_project_code: v }));
                          }}
                          className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                        />
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs text-slate-500 dark:text-slate-400">{t('operations.alphabeticCharsHint')}</p>
                        <div className="flex items-center gap-3">
                          <span
                            className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer"
                            onClick={() => setNewWorkflowForm((f) => ({ ...f, allow_alphabetic_chars: !f.allow_alphabetic_chars }))}
                          >
                            {t('operations.allowAlphabeticChars')}
                          </span>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={newWorkflowForm.allow_alphabetic_chars}
                              onChange={(e) => setNewWorkflowForm((f) => ({ ...f, allow_alphabetic_chars: e.target.checked }))}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!newWorkflowForm.code?.trim()) {
                            setWorkflowAddError(t('operations.workflowNameRequired'));
                            return;
                          }
                          setWorkflowAddError(null);
                          setAddingWorkflow(true);
                          try {
                            await createWorkflow({
                              code: newWorkflowForm.code.trim().toUpperCase(),
                              label: newWorkflowForm.code.trim().toUpperCase(),
                              project_code: newWorkflowForm.project_code?.trim() || '',
                              digits_in_project_code: newWorkflowForm.digits_in_project_code,
                              allow_alphabetic_chars: newWorkflowForm.allow_alphabetic_chars,
                            });
                            setNewWorkflowForm({ code: '', project_code: '', digits_in_project_code: 7, allow_alphabetic_chars: true });
                            const data = await getWorkflows();
                            const list = Array.isArray(data) ? data : (data?.results ?? []);
                            setWorkflowsList(list);
                            setAvailableWorkflows(list);
                          } catch (err) {
                            setWorkflowAddError(formatApiError(err));
                          } finally {
                            setAddingWorkflow(false);
                          }
                        }}
                        disabled={addingWorkflow}
                        className="w-full h-10 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                      >
                        <span className="material-symbols-outlined text-[18px]">add</span>
                        {addingWorkflow ? t('operations.addingWorkflow') : t('operations.addWorkflow')}
                      </button>
                    </div>
                  </div>

                  {/* Existing Workflows List */}
                  <div className=" border-slate-200 dark:border-slate-600 pt-4 flex-1">
                    <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3">{t('operations.existingWorkflows')}</p>
                    <ul className="space-y-2">
                      {workflowsList.length === 0 ? (
                        <li className="text-sm text-slate-500 dark:text-slate-400 py-4 text-center">{t('operations.noWorkflows')}</li>
                      ) : (
                        workflowsList.map((w) => (
                          <li
                            key={w.code || w.id}
                            className="flex items-center gap-3 px-4 py-3 rounded-lg bg-slate-50 dark:bg-slate-700/50 border border-slate-100 dark:border-slate-600"
                          >
                            <span className="material-symbols-outlined text-slate-500 dark:text-slate-400 text-[20px]">account_tree</span>
                            <div className="flex-1 min-w-0">
                              <span className="font-semibold text-slate-900 dark:text-slate-100">{w.code}</span>
                              {w.label && w.label !== w.code && (
                                <span className="text-slate-500 dark:text-slate-400 text-sm ml-2">— {w.label}</span>
                              )}
                              {(w.project_code || w.digits_in_project_code != null || w.allow_alphabetic_chars === false) && (
                                <span className="text-slate-500 dark:text-slate-400 text-xs ml-2 block mt-0.5">
                                  {t('operations.workflowMeta', { code: w.project_code || '—', digits: w.digits_in_project_code ?? '—' })}
                                  {w.allow_alphabetic_chars === false && ` · ${t('operations.digitsOnly')}`}
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingWorkflowId(w.id);
                                  setEditWorkflowForm({
                                    code: w.code || '',
                                    label: w.label || w.code || '',
                                    project_code: w.project_code || '',
                                    digits_in_project_code: w.digits_in_project_code ?? 7,
                                    allow_alphabetic_chars: w.allow_alphabetic_chars !== false,
                                  });
                                  setWorkflowAddError(null);
                                }}
                                className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-500 hover:text-primary hover:bg-slate-100 dark:hover:bg-slate-700"
                                title={t('common:edit')}
                              >
                                <span className="material-symbols-outlined text-[18px]">edit</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => setWorkflowToDelete(w)}
                                className="h-8 w-8 flex items-center justify-center rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                title={t('common:delete')}
                              >
                                <span className="material-symbols-outlined text-[18px]">delete</span>
                              </button>
                            </div>
                          </li>
                        ))
                      )}
                    </ul>
                  </div>

                  {/* Edit Workflow Modal */}
                  {editingWorkflowId && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50" onClick={() => setEditingWorkflowId(null)}>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-xl w-full border border-slate-200 dark:border-slate-600 overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="p-6 border-b border-slate-100 dark:border-slate-600 flex justify-between items-start">
                          <div>
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">{t('operations.editWorkflow')}</p>
                            <h3 className="text-xl font-bold text-slate-900 dark:text-slate-100">{t('operations.updateWorkflow')}</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                              {t('operations.editWorkflowSubtitle')}
                            </p>
                          </div>
                          <button
                            onClick={() => setEditingWorkflowId(null)}
                            className="h-8 w-8 flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
                            title={t('common:close')}
                          >
                            <span className="material-symbols-outlined text-[22px]">close</span>
                          </button>
                        </div>
                        <div className="p-6 space-y-4">
                          {workflowAddError && (
                            <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700/50 text-red-700 dark:text-red-200 text-sm">
                              {workflowAddError}
                            </div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('operations.workflowName')} *</label>
                              <input
                                type="text"
                                value={editWorkflowForm.code}
                                onChange={(e) => setEditWorkflowForm((f) => ({ ...f, code: e.target.value.trim().toUpperCase() }))}
                                placeholder={t('operations.workflowNamePlaceholder')}
                                className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('operations.label')}</label>
                              <input
                                type="text"
                                value={editWorkflowForm.label}
                                onChange={(e) => setEditWorkflowForm((f) => ({ ...f, label: e.target.value }))}
                                placeholder={t('operations.labelPlaceholder')}
                                className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('operations.projectCode')}</label>
                              <input
                                type="text"
                                value={editWorkflowForm.project_code}
                                onChange={(e) => setEditWorkflowForm((f) => ({ ...f, project_code: e.target.value }))}
                                placeholder={t('operations.projectCodePlaceholder')}
                                className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                              />
                            </div>
                            <div className="space-y-2">
                              <label className="text-sm font-semibold text-slate-900 dark:text-slate-100 block">{t('operations.digitsInProjectCodeShort')}</label>
                              <input
                                type="number"
                                min={0}
                                max={20}
                                value={editWorkflowForm.digits_in_project_code}
                                onChange={(e) => {
                                  const v = parseInt(e.target.value, 10);
                                  if (!isNaN(v) && v >= 0 && v <= 20) setEditWorkflowForm((f) => ({ ...f, digits_in_project_code: v }));
                                }}
                                className="w-full rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/50 p-3 text-sm text-slate-900 dark:text-slate-100 focus:border-primary focus:ring-1 focus:ring-primary outline-none"
                              />
                            </div>
                            <div className="space-y-2">
                              <p className="text-xs text-slate-500 dark:text-slate-400">{t('operations.alphabeticCharsHint')}</p>
                              <div className="flex items-center gap-3">
                                <span
                                  className="text-sm font-medium text-slate-700 dark:text-slate-300 cursor-pointer"
                                  onClick={() => setEditWorkflowForm((f) => ({ ...f, allow_alphabetic_chars: !f.allow_alphabetic_chars }))}
                                >
                                  {t('operations.allowAlphabeticChars')}
                                </span>
                                <label className="toggle-switch">
                                  <input
                                    type="checkbox"
                                    checked={editWorkflowForm.allow_alphabetic_chars}
                                    onChange={(e) => setEditWorkflowForm((f) => ({ ...f, allow_alphabetic_chars: e.target.checked }))}
                                  />
                                  <span className="toggle-slider"></span>
                                </label>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 pt-2">
                            <button
                              type="button"
                              onClick={async () => {
                                if (!editWorkflowForm.code?.trim()) return;
                                setWorkflowAddError(null);
                                try {
                                  await updateWorkflow(editingWorkflowId, {
                                    code: editWorkflowForm.code.trim().toUpperCase(),
                                    label: editWorkflowForm.label?.trim() || editWorkflowForm.code.trim().toUpperCase(),
                                    project_code: editWorkflowForm.project_code?.trim() || '',
                                    digits_in_project_code: editWorkflowForm.digits_in_project_code,
                                    allow_alphabetic_chars: editWorkflowForm.allow_alphabetic_chars,
                                  });
                                  setEditingWorkflowId(null);
                                  const data = await getWorkflows();
                                  const list = Array.isArray(data) ? data : (data?.results ?? []);
                                  setWorkflowsList(list);
                                  setAvailableWorkflows(list);
                                } catch (err) {
                                  setWorkflowAddError(formatApiError(err));
                                }
                              }}
                              className="flex-1 h-10 rounded-lg bg-primary text-white font-semibold text-sm hover:bg-primary-hover"
                            >
                              {t('operations.saveChanges')}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setEditingWorkflowId(null); setWorkflowAddError(null); }}
                              className="h-10 px-5 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium hover:bg-slate-100 dark:hover:bg-slate-700"
                            >
                              {t('common:cancel')}
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  )}

                  {/* Delete confirmation */}
                  {workflowToDelete && (
                    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
                      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl max-w-sm w-full p-6 border border-slate-200 dark:border-slate-600">
                        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-2">{t('operations.deleteWorkflowTitle')}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                          {t('operations.deleteWorkflowConfirm', { code: workflowToDelete.code })}
                        </p>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              setDeletingWorkflow(true);
                              try {
                                await deleteWorkflow(workflowToDelete.id);
                                setWorkflowToDelete(null);
                                const data = await getWorkflows();
                                const list = Array.isArray(data) ? data : (data?.results ?? []);
                                setWorkflowsList(list);
                                setAvailableWorkflows(list);
                              } catch (err) {
                                setWorkflowAddError(formatApiError(err));
                              } finally {
                                setDeletingWorkflow(false);
                              }
                            }}
                            disabled={deletingWorkflow}
                            className="flex-1 h-9 rounded-lg bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-50"
                          >
                            {deletingWorkflow ? t('common:deleting') : t('common:delete')}
                          </button>
                          <button
                            type="button"
                            onClick={() => setWorkflowToDelete(null)}
                            disabled={deletingWorkflow}
                            className="h-9 px-4 rounded-lg border border-slate-200 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium"
                          >
                            {t('common:cancel')}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteOperationModal && selectedOperation && selectedOperationData && (
          <div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60"
            onClick={() => {
              if (!deleteSubmitLoading && !deleteInfoLoading) setShowDeleteOperationModal(false);
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.15 }}
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-slate-200 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-lg font-bold text-slate-900 mb-1">{t('operations.deleteOperation')}</h3>
              <p className="text-sm text-slate-600 mb-4">
                <span className="font-medium text-slate-800">{selectedOperationData.activity}</span>
              </p>
              {deleteInfoLoading ? (
                <p className="text-sm text-slate-500 py-4">{t('operations.loadingRelatedEntries')}</p>
              ) : (
                <>
                  {deleteModalError && (
                    <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
                      {deleteModalError}
                    </div>
                  )}
                  {operationDeleteInfo && (
                    <p className="text-sm text-slate-700 mb-4">
                      {t('operations.relatedImputations')}{' '}
                      <span className="font-semibold">{operationDeleteInfo.imputation_count}</span>
                    </p>
                  )}
                  {operationDeleteInfo && operationDeleteInfo.imputation_count > 0 && (
                    <div className="space-y-3 mb-4">
                      <p className="text-sm font-medium text-slate-800">{t('operations.relatedImputationsPrompt')}</p>
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="deleteRelatedMode"
                          className="mt-1"
                          checked={deleteRelatedMode === 'reassign'}
                          onChange={() => setDeleteRelatedMode('reassign')}
                          disabled={deleteSubmitLoading}
                        />
                        <span className="text-sm text-slate-700">
                          {t('operations.reassignImputations')}
                          {operations.filter((o) => o.id !== selectedOperation).length === 0 && (
                            <span className="block text-amber-700 text-xs mt-1">
                              {t('operations.noOtherOperationInCatalog')}
                            </span>
                          )}
                        </span>
                      </label>
                      {deleteRelatedMode === 'reassign' && operations.filter((o) => o.id !== selectedOperation).length > 0 && (
                        <select
                          value={deleteTargetOperationId}
                          onChange={(e) => setDeleteTargetOperationId(e.target.value)}
                          disabled={deleteSubmitLoading}
                          className="w-full h-11 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-900"
                        >
                          <option value="">{t('operations.selectTargetOperation')}</option>
                          {operations
                            .filter((o) => o.id !== selectedOperation)
                            .map((o) => (
                              <option key={o.id} value={String(o.id)}>
                                {o.activity}
                                {o.program ? ` — ${o.program}` : ''}
                              </option>
                            ))}
                        </select>
                      )}
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="deleteRelatedMode"
                          className="mt-1"
                          checked={deleteRelatedMode === 'delete'}
                          onChange={() => setDeleteRelatedMode('delete')}
                          disabled={deleteSubmitLoading}
                        />
                        <span className="text-sm text-slate-700">{t('operations.deleteRelatedImputations')}</span>
                      </label>
                    </div>
                  )}
                  {operationDeleteInfo && operationDeleteInfo.imputation_count === 0 && (
                    <p className="text-sm text-slate-600 mb-4">{t('operations.noImputationsSafeDelete')}</p>
                  )}
                </>
              )}
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={handleConfirmDeleteOperation}
                  disabled={deleteSubmitLoading || deleteInfoLoading || !operationDeleteInfo}
                  className="flex-1 h-10 rounded-lg bg-red-600 text-white font-semibold text-sm hover:bg-red-700 disabled:opacity-50"
                >
                  {deleteSubmitLoading ? t('common:deleting') : t('operations.confirmDeleteAction')}
                </button>
                <button
                  type="button"
                  onClick={() => !deleteSubmitLoading && setShowDeleteOperationModal(false)}
                  disabled={deleteSubmitLoading}
                  className="h-10 px-4 rounded-lg border border-slate-200 text-slate-700 text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
                >
                  {t('common:cancel')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OperationsPage;

